/**
 * Datasource operations for CDP SDK.
 */

import { CDPSession, getSession } from '../session';

export class DatasourceManager {
  constructor(private readonly session: CDPSession = getSession()) {}

  async listDatasources(
    projectUuid: string,
    page = 1,
    size = 20,
    dsType?: string,
    name?: string,
  ): Promise<Record<string, any>> {
    const options: Record<string, string> = { projectUuid };
    if (dsType) {
      options.dsType = dsType;
    }
    if (name) {
      options.name = name;
    }
    const params: Record<string, any> = { current: page, size, options: JSON.stringify(options) };
    return this.session.get('/api/cdp/datasource/extend/page', { params });
  }

  async getDatasource(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/datasource/${uuid}`);
  }

  async createDatasource(
    projectUuid: string,
    name: string,
    dsType: string,
    config: Record<string, any>,
    description?: string,
  ): Promise<Record<string, any>> {
    const data = {
      data: {
        projectUuid,
        name,
        dsType,
        config,
        description,
      },
    };
    return this.session.post('/api/cdp/datasource', { data });
  }

  async updateDatasource(
    uuid: string,
    name?: string,
    config?: Record<string, any>,
    description?: string,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { data: { uuid } };
    if (name) data.data.name = name;
    if (config) data.data.config = config;
    if (description) data.data.description = description;
    return this.session.put('/api/cdp/datasource', { data });
  }

  async deleteDatasource(uuid: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/cdp/datasource/${uuid}`);
  }

  async testConnection(dsType: string, config: Record<string, any>): Promise<boolean> {
    const data = { dsType, config };
    const result = await this.session.post('/api/cdp/datasource/test', { data });
    return result?.success ?? false;
  }

  async getTables(uuid: string): Promise<Record<string, any>[]> {
    const result = await this.session.get(`/api/cdp/jdbc/${uuid}/tables`);
    return result?.data ?? [];
  }

  async getColumns(uuid: string, tableName: string): Promise<Record<string, any>[]> {
    const result = await this.session.get(`/api/cdp/jdbc/${uuid}/tableFields`, {
      params: { tableName },
    });
    return result?.data ?? [];
  }

  async syncDevices(uuid: string, deviceTypeUuid: string): Promise<Record<string, any>> {
    const data = { datasourceUuid: uuid, deviceTypeUuid };
    return this.session.post('/api/cdp/datasource/syncDevices', { data });
  }

  // --- Extended datasource APIs ---

  async createH2Datasource(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/datasource/createH2Datasource', { data });
  }

  async saveGisDatasource(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/datasource/saveGisDatasource', { data });
  }

  async requestApi(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/datasource/request/api', { data: { context: {}, data } });
  }

  async getJsonSchema(data?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/datasource/getJSONSchema', { data: { context: {}, data } });
  }

  async getJsonSchema2(data?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/datasource/getJSONSchema2', { data: { context: {}, data } });
  }

  async getDatasetTable(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/datasource/getDatasetTable', { params });
  }

  async testWsConnect(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/ws/test-connect', { params });
  }

  async testStompConnect(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/stomp/test-connect', { params });
  }

  async testMqttConnect(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/mqtt/test-connect', { data });
  }

  async testKafkaConnect(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/kafka/test-connect', { data });
  }

  async testJdbcConnect(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/jdbc/test-connect', { params });
  }

  async testH2Connect(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/jdbc/test-h2connect', { data });
  }

  async testRedisConnect(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/redis/test-connect', { params });
  }

  async listJdbcTables(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/jdbc/tables', { params });
  }

  async checkExist(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/datasource/exist', { params });
  }

  async getFileJson(data?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/datasource/getFileJson', { data });
  }

  async createStaticDatasource(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/datasource/createStatic', { data: { context: {}, data } });
  }

  async updateStaticDatasource(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/datasource/updateStatic', { data: { context: {}, data } });
  }

  async getStaticJson(fileName: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/datasource/getStaticJson/${fileName}`);
  }

  async copyDatasource(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/datasource/copy', { data });
  }

  /** Export datasource as Blob. */
  async exportDatasource(data?: Record<string, any>): Promise<Blob> {
    return this.session.request('POST', '/api/cdp/datasource/export', {
      data,
      responseType: 'blob',
    });
  }

  async importDatasource(file: File | Blob, params?: Record<string, any>): Promise<Record<string, any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.session.post('/api/cdp/datasource/import', {
      data: formData,
      params,
      headers: {},
    });
  }
}
